import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import AdminHeader from "./AdminHeader";
import { X } from "lucide-react";
import styles from "../../style/admin.module.css";
import { useSettingsQuery } from "../../api/query/useAdminQueries";
import { useAuthStore } from "../../hooks/useAuthStore";
import { useIdleLogout } from "../../hooks/useIdleLogout";
import { parseDurationToMs } from "../../utils/duration";
import { useToast } from "../../hooks/useToast";
import {
  useNotificationsQuery,
  useUnreadNotificationsCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} from "../../api/query/useNotificationQueries";
import {
  formatNotificationTime,
} from "../../utils/notifications";
import NotificationAvatar from "../NotificationAvatar";

function AdminLayout({ children, headerTitle, headerSubtitle }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const { data: notificationsData } = useNotificationsQuery();
  const notifications = notificationsData?.data?.items || [];

  const { data: unreadCountData } = useUnreadNotificationsCountQuery();

  const markAllNotificationsReadMutation = useMarkAllNotificationsReadMutation();
  const markNotificationReadMutation = useMarkNotificationReadMutation();

  const navigate = useNavigate();
  const toast = useToast();
  const { actions } = useAuthStore();
  const { data: settingsResponse } = useSettingsQuery();
  const autoTimeoutMs = parseDurationToMs(
    settingsResponse?.data?.securitySettings?.autoTimeoutDuration
  );

  // Backs Settings > Security Settings > Auto-timeout Duration: signs the
  // admin out after the configured period of inactivity instead of leaving
  // the setting unenforced.
  useIdleLogout(autoTimeoutMs, async () => {
    await actions.logout();
    toast.success("You were signed out after a period of inactivity.");
    navigate("/auth/admin/login", { replace: true });
  });

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);
  const closeNotifications = () => setIsNotificationsOpen(false);
  const toggleNotifications = () => {
    closeSidebar();
    setIsNotificationsOpen((prev) => !prev);
  };

  const unreadNotificationsCount = unreadCountData?.data?.count || 0;

  const markAllNotificationsAsRead = () => {
    markAllNotificationsReadMutation.mutate();
  };

  const handleMarkOneAsRead = (notification) => {
    if (!notification.isRead) {
      markNotificationReadMutation.mutate(notification._id);
    }
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSidebarOpen(false);
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <div className={styles.adminLayout}>
      <div
        className={`flex h-full flex-col ${
          isNotificationsOpen ? "pointer-events-none" : ""
        }`}
      >
        <AdminHeader
          title=""
          subtitle=""
          showControls={false}
          showTitle={false}
          onNotificationsClick={toggleNotifications}
          notificationCount={unreadNotificationsCount}
          notificationsOpen={isNotificationsOpen}
          onMenuClick={toggleSidebar}
        />
        <AdminSidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        <div className={styles.adminMain}>
          <main className={styles.adminContent}>{children}</main>
        </div>
      </div>

      {/* Notification side drawer */}
      <div
        className={`fixed inset-0 z-[1100] bg-[#0F172A]/50 backdrop-blur-[5px] transition-opacity duration-200 ${
          isNotificationsOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={closeNotifications}
      />
      <aside
        className={`fixed inset-y-0 right-0 z-[1101] flex h-screen w-full max-w-[400px] flex-col overflow-hidden border-l border-[#EAECF0] bg-white shadow-[-20px_0_60px_rgba(16,24,40,0.18)] transition-transform duration-200 ${
          isNotificationsOpen ? "translate-x-0" : "pointer-events-none translate-x-full"
        }`}
        aria-hidden={!isNotificationsOpen}
      >
        <div className="border-b border-[#F2F4F7] px-5 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-[18px] font-semibold leading-none text-[#101828]">
                Notifications
              </h2>
              <p className="mt-3 text-sm text-[#98A2B3]">
                Stay updated with important alerts and activity
              </p>
            </div>

            <button
              type="button"
              onClick={closeNotifications}
              className="rounded-md p-1.5 text-[#98A2B3] transition hover:bg-gray-50 hover:text-[#475467]"
              aria-label="Close notifications"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end border-b border-[#F2F4F7] px-5 py-4">
          <button
            type="button"
            onClick={markAllNotificationsAsRead}
            className="text-sm font-semibold text-[#61013B] disabled:cursor-not-allowed disabled:text-[#98A2B3]"
            disabled={unreadNotificationsCount === 0}
          >
            Mark all as read
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map((notification, index) => (
              <div
                key={notification._id || notification.id}
                onClick={() => handleMarkOneAsRead(notification)}
                className={`flex cursor-pointer gap-3 px-5 py-5 transition ${
                  !notification.isRead
                    ? "bg-[#FAF7F9] hover:bg-[#F5EFF3]"
                    : "hover:bg-gray-50"
                } ${
                  index < notifications.length - 1 ? "border-b border-[#F2F4F7]" : ""
                }`}
              >
                <div className="flex pt-2.5">
                  <span
                    className={`h-3 w-3 rounded-full ${
                      !notification.isRead ? "bg-[#7A0147]" : "bg-[#EAECF0]"
                    }`}
                  />
                </div>

                <NotificationAvatar
                  notification={notification}
                  className="h-14 w-14"
                />

                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold leading-7 text-[#101828]">
                    {notification.title}
                  </p>
                  <p className="mt-1 text-sm font-medium leading-6 text-[#475467]">
                    {notification.body}
                  </p>
                  <p className="mt-3 text-sm font-medium text-[#98A2B3]">
                    {formatNotificationTime(notification.createdAt)}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="flex h-full items-center justify-center px-6 py-10 text-center">
              <p className="text-sm font-medium text-[#667085]">
                You have no notifications yet.
              </p>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

export default AdminLayout;
